import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { GraduationCap, Briefcase, Building, Loader2, FileText } from 'lucide-react';

export function RoleSelection({ signupIntent }: { signupIntent?: string | null }) {
  const { updateUserRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'learner' | 'professional' | 'tutor' | 'org' | null>(null);

  useEffect(() => {
    // Optional: Preselect based on intent, but let user confirm
    if (signupIntent === 'self_learn_from_document') {
      setSelectedRole('professional');
    } else if (signupIntent === 'create_course_for_others') {
      setSelectedRole('tutor');
    }
  }, [signupIntent]);

  const handleSubmit = async () => {
    if (!selectedRole) return;
    setLoading(true);
    try {
      if (selectedRole === 'learner') {
        await updateUserRole('learner', false, 'learner');
      } else if (selectedRole === 'professional') {
        // We set their db role to learner, but identify them as professional
        await updateUserRole('learner', false, 'professional');
      } else if (selectedRole === 'tutor') {
        await updateUserRole('tutor', false, 'tutor');
      } else if (selectedRole === 'org') {
        await updateUserRole('tutor', true, 'org');
      }
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] p-4 text-center bg-bg-main relative z-50">
      <div className="bg-bg-alt border border-border-main p-8 rounded-3xl max-w-4xl w-full shadow-2xl relative overflow-hidden">
        <h2 className="text-3xl font-bold text-text-main mb-3">Welcome to Lumina Learn</h2>
        <p className="text-text-alt mb-8 text-lg">How do you want to use the platform?</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-left">
          <label 
            className={`cursor-pointer transition-all border p-6 rounded-2xl flex items-start gap-4 ${
              selectedRole === 'professional' 
                ? 'border-indigo-500 bg-indigo-500/10 shadow-[0_0_20px_rgba(99,102,241,0.15)] ring-2 ring-indigo-500 ring-offset-2 ring-offset-bg-alt' 
                : 'border-border-main hover:border-indigo-400/50 hover:bg-bg-main'
            }`}
          >
            <input type="radio" name="role" className="hidden" checked={selectedRole === 'professional'} onChange={() => setSelectedRole('professional')} />
            <div className={`p-4 rounded-xl shrink-0 ${selectedRole === 'professional' ? 'bg-indigo-500 text-white' : 'bg-bg-main text-text-main'}`}>
              <FileText className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold text-text-main text-lg mb-1">Professional / Self-Learner</h3>
              <p className="text-sm text-text-alt">Upload your own work documents, reports, and policies to learn quickly.</p>
            </div>
          </label>

          <label 
            className={`cursor-pointer transition-all border p-6 rounded-2xl flex items-start gap-4 ${
              selectedRole === 'tutor' 
                ? 'border-emerald-500 bg-emerald-500/10 shadow-[0_0_20px_rgba(16,185,129,0.15)] ring-2 ring-emerald-500 ring-offset-2 ring-offset-bg-alt' 
                : 'border-border-main hover:border-emerald-400/50 hover:bg-bg-main'
            }`}
          >
            <input type="radio" name="role" className="hidden" checked={selectedRole === 'tutor'} onChange={() => setSelectedRole('tutor')} />
            <div className={`p-4 rounded-xl shrink-0 ${selectedRole === 'tutor' ? 'bg-emerald-500 text-white' : 'bg-bg-main text-text-main'}`}>
              <Briefcase className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold text-text-main text-lg mb-1">Tutor / Creator</h3>
              <p className="text-sm text-text-alt">Create courses from documents and share them with your students or clients.</p>
            </div>
          </label>

          <label 
            className={`cursor-pointer transition-all border p-6 rounded-2xl flex items-start gap-4 ${
              selectedRole === 'learner' 
                ? 'border-indigo-500 bg-indigo-500/10 shadow-[0_0_20px_rgba(99,102,241,0.15)] ring-2 ring-indigo-500 ring-offset-2 ring-offset-bg-alt' 
                : 'border-border-main hover:border-indigo-400/50 hover:bg-bg-main'
            }`}
          >
            <input type="radio" name="role" className="hidden" checked={selectedRole === 'learner'} onChange={() => setSelectedRole('learner')} />
            <div className={`p-4 rounded-xl shrink-0 ${selectedRole === 'learner' ? 'bg-indigo-500 text-white' : 'bg-bg-main text-text-main'}`}>
              <GraduationCap className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold text-text-main text-lg mb-1">Assigned Learner</h3>
              <p className="text-sm text-text-alt">Take assigned courses, study lessons, and complete knowledge checks.</p>
            </div>
          </label>

          <label 
            className={`cursor-pointer transition-all border p-6 rounded-2xl flex items-start gap-4 ${
              selectedRole === 'org' 
                ? 'border-rose-500 bg-rose-500/10 shadow-[0_0_20px_rgba(244,63,94,0.15)] ring-2 ring-rose-500 ring-offset-2 ring-offset-bg-alt' 
                : 'border-border-main hover:border-rose-400/50 hover:bg-bg-main'
            }`}
          >
            <input type="radio" name="role" className="hidden" checked={selectedRole === 'org'} onChange={() => setSelectedRole('org')} />
            <div className={`p-4 rounded-xl shrink-0 ${selectedRole === 'org' ? 'bg-rose-500 text-white' : 'bg-bg-main text-text-main'}`}>
              <Building className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold text-text-main text-lg mb-1">Organisation / Training Team</h3>
              <p className="text-sm text-text-alt">Manage learning for a team. Create shared courses and track progress.</p>
            </div>
          </label>
        </div>

        <button
          onClick={handleSubmit}
          disabled={!selectedRole || loading}
          className="w-full sm:w-auto px-12 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mx-auto"
        >
          {loading && <Loader2 className="h-5 w-5 animate-spin" />}
          Continue to Lumina
        </button>
      </div>
    </div>
  );
}
